<template lang="pug">
mixin dot-center
  span.text-base.inline-flex.items-center.relative()&attributes(attributes)
    uc-icon.absolute.inset-0.scale-150(
      class="text-primary-500/10"
      icon="mdi:circle"
    )
    block

.screen-quiz-progress.fixed.w-full.z-50.pointer-events-none(
  uc-dont-read=""
  uc-word-skip=""
)
  .quiz-nav.opacity-0.z-10.top-0.flex.justify-center
    .w-fit.flex.flex-wrap.justify-center.h2.mx-6.p-1.px-2.rounded-full.bg-gray-50.bg-opacity-95.shadow(
      :class="[isEditModeAvailable ? 'mt-12' : 'mt-4']"
    )
      +dot-center.mx-1.my-2(
        :data-index="i"
        :id="'quiz-dot-' + i"
        :key="q.id"
        v-for="(q, i) in includedQuestions"
        v-if="!wordDoc && !audioDoc"
      )
        +dot-center.relative.text-primary-800(
          v-if="isCorrect(q)"
        ) 
          transition-pop
            uc-icon.scale-75(icon="mdi:check-thick")
        +dot-center.relative.text-red-500(
          v-else-if="isSubmitted(q)"
        ) 
          transition-pop
            uc-icon.scale-75(icon="mdi:close-thick")
        +dot-center(class="text-black/30" v-else="") 
          uc-icon(icon="mdi:circle")
</template>
<script setup lang="ts">
import { inject, ref, unref, onMounted, computed, watch } from 'vue';
import {
  useEventListener,
  breakpointsTailwind,
  useBreakpoints
} from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { debounce } from 'lodash-es';
import useQuizzes from '../stores/useQuizzes';
import useContent from '../stores/useContent';
import useSuspend from '../lib/useSuspend';
import useFilterTags from '../lib/useFilterTags';
import isQuizPage from '../lib/isQuizPage';

const props = defineProps<{
  pageId: string;
}>();

const { compiled: content } = storeToRefs(useContent());
const suspend = useSuspend();
const audioDoc = inject('audioDoc') as boolean;
const wordDoc = unref<boolean>(inject('wordDoc'));
const isEditModeAvailable = inject('isEditModeAvailable') as Ref<boolean>;
const includedQuestions = ref([]);
const progressBarAnimated = ref(false);
const currentSection = ref<HTMLElement | null>(null);
const pageHasQuizProgress = ref<boolean>(false);

const { computeQuizQuestionStatuses } = storeToRefs(useQuizzes());
const breakpoints = useBreakpoints(breakpointsTailwind);
const largeDevice = breakpoints.greater('md');

let tl;

// TODO: use this to filter tags
const filterTags = useFilterTags();

const isSubmitted = function (q) {
  return suspend.completed[q.id];
};

const isCorrect = function (q) {
  return suspend.correctQuestions[q.id];
};

const currentPage = computed(() => {
  return props.pageId;
});

const includedQuizzes = computed(() => {
  return content.value.course && content.value.course['quiz-progress']
    ? content.value.course['quiz-progress'].split(/\s+/)
    : [];
});

const setupBarTimeline = () => {
  const fromOpacity = 0;
  const toOpacity = 1;
  const fromY = -56;
  const toY = 0;

  tl = gsap.timeline({ paused: true });
  tl.fromTo(
    '.quiz-nav',
    { y: fromY, autoAlpha: fromOpacity },
    {
      y: toY,
      autoAlpha: toOpacity,
      duration: 1,
      ease: 'elastic.out(1.25,1)',
      stagger: 0.25
    }
  );
};

onMounted(async () => {
  pageHasQuizProgress.value = await isQuizPage(
    includedQuizzes.value,
    currentPage.value
  );
  if (pageHasQuizProgress.value) {
    const quizzesFromStore = computeQuizQuestionStatuses.value;
    includedQuizzes.value.forEach(q => {
      if (quizzesFromStore.has(q)) {
        quizzesFromStore.get(q)?.forEach(question => {
          includedQuestions.value.push(question);
        });
      }
    });

    if (includedQuizzes.value.length > 0) {
      useEventListener(window, 'scrollsectionentered', e => {
        currentSection.value = e.target as HTMLElement;
        showOrHideQuizProgress();
      });
    }
    setupBarTimeline();
  }
});

const animateProgressBar = newValue => {
  if (!tl) return;
  progressBarAnimated.value = newValue;
  tl.delay(newValue ? 1 : 0);
  tl.timeScale(newValue ? 1 : 4);

  if (suspend.reducedMotion) {
    tl.progress(newValue ? 1 : 0);
  }

  newValue ? tl.play() : tl.reverse();
};

// show/hide the badges side bar depending on screen type
// TODO: this is a bit hacky, showQuizProgress should select on more that just the presence of a question
const showOrHideQuizProgress = debounce(
  () => {
    if (
      !pageHasQuizProgress.value ||
      currentSection.value === null ||
      !largeDevice.value
    )
      return;

    const questionScreenEl = currentSection.value?.querySelector(
      '.screen--question, .ordering--question, .grid--question, .email--question, .voicemail--question'
    );

    const showQuizProgress =
      questionScreenEl &&
      includedQuestions.value?.some(q => q?.id === questionScreenEl?.id);

    if (showQuizProgress) {
      if (!progressBarAnimated.value) {
        animateProgressBar(true);
      }
    } else {
      animateProgressBar(false);
    }
  },
  300,
  { trailing: true }
);

watch(largeDevice, () => {
  if (!pageHasQuizProgress.value || currentSection.value === null) return;
  animateProgressBar(largeDevice.value);
});
</script>
